import { SignupSource } from '../../../common/enums/signupSource.enum';

const WEBSITE_STEPS = [
  'referring-channel',
  'gift-card-reward',
  'waiting-list',
  'location-onboarding',
  'download',
];

const APP_STEPS = [
  'referring-channel',
  'gift-card-reward',
  'waiting-list',
  'location-onboarding',
];

/** Live users skip download only when signup was started from the app. */
export function shouldSkipOnboardingDownload({
  hasRedirectUri,
}: {
  hasRedirectUri: boolean;
}): boolean {
  return hasRedirectUri;
}

export function getOnboardingSteps(
  skipDownload: boolean,
  signUpSource: string,
  specialSourceOnboarded: boolean,
): string[] {
  const isSpecialSource =
    signUpSource === SignupSource.QUICKMEMECOIN ||
    signUpSource === SignupSource.CRYPTOBULLSVIP ||
    signUpSource === SignupSource.QUICKCAMCOIN;

  if (isSpecialSource) {
    if (specialSourceOnboarded) {
      return ['waiting-list', 'download'];
    }
    return ['referring-channel', 'gift-card-reward', 'location-onboarding'];
  }

  return skipDownload ? APP_STEPS : WEBSITE_STEPS;
}

export type OnboardingHashResolution =
  | { kind: 'step'; index: number }
  | { kind: 'invalid' };

export function resolveOnboardingHashStep(
  hashStep: string,
  steps: string[],
): OnboardingHashResolution {
  const index = steps.indexOf(hashStep);
  if (index >= 0) {
    return { kind: 'step', index };
  }
  return { kind: 'invalid' };
}

export function buildAppReturnHref(
  redirectUrl: string,
  referringChannel: string,
  channelId: string,
): string {
  return `${redirectUrl}?refferingChannel=${referringChannel}&channelId=${channelId}`;
}

export type OnboardingNavConfig = {
  showPrevious: boolean;
  showNext: boolean;
};

/** Footer nav: page 1 has no Previous/Next; last step Previous only; middle steps both. */
export function getOnboardingNavConfig(
  activeStep: number,
  steps: string[],
): OnboardingNavConfig {
  if (activeStep <= 0 || steps.length === 0) {
    return { showPrevious: false, showNext: false };
  }

  const isLastStep = activeStep >= steps.length - 1;

  return {
    showPrevious: true,
    showNext: !isLastStep,
  };
}

export const ONBOARDING_AUTO_SKIP_STORAGE_KEY = 'onboarding-auto-skipped-referrer';
export const ONBOARDING_ALLOW_REFERRER_STEP_KEY = 'onboarding-allow-referrer-step';
export const ONBOARDING_REFERRER_CLEARED_KEY = 'onboarding-referrer-cleared';

export function readOnboardingAutoSkippedReferrer(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(ONBOARDING_AUTO_SKIP_STORAGE_KEY) === '1';
}

export function markOnboardingAutoSkippedReferrer(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(ONBOARDING_AUTO_SKIP_STORAGE_KEY, '1');
}

export function readOnboardingAllowReferrerStep(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(ONBOARDING_ALLOW_REFERRER_STEP_KEY) === '1';
}

export function markOnboardingAllowReferrerStep(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(ONBOARDING_ALLOW_REFERRER_STEP_KEY, '1');
}

export function isOnboardingReferrerCleared(channel?: string | null): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  const cleared = sessionStorage.getItem(ONBOARDING_REFERRER_CLEARED_KEY);
  if (cleared === null) {
    return false;
  }
  return !channel || cleared === channel.trim().toLowerCase();
}

export function markOnboardingReferrerCleared(channel: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(
    ONBOARDING_REFERRER_CLEARED_KEY,
    channel.trim().toLowerCase(),
  );
}

export function unmarkOnboardingReferrerCleared(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(ONBOARDING_REFERRER_CLEARED_KEY);
}

/** Live onboarding only: skip referring-channel once when user already has a referrer. */
export function shouldAutoSkipReferringChannel({
  hasReferrer,
  activeStep,
  firstStep,
  alreadyAutoSkipped,
  hashStep,
  userAllowReferrerStep,
}: {
  hasReferrer: boolean;
  activeStep: number;
  firstStep: string | undefined;
  alreadyAutoSkipped: boolean;
  hashStep: string;
  userAllowReferrerStep: boolean;
}): boolean {
  if (alreadyAutoSkipped || userAllowReferrerStep) {
    return false;
  }
  const isInitialEntry = !hashStep;
  return Boolean(
    hasReferrer &&
      activeStep === 0 &&
      firstStep === 'referring-channel' &&
      isInitialEntry,
  );
}
